import React, { useState } from 'react';
import { useGoogleDriveToken } from '../../../features/docs/googleToken';
import { DriveFileList } from '../../../features/docs/DriveFileList';
import { canonicalDriveFolderUrl } from '../../../features/docs/drive';
import { useDriveFiles, useMerchantDriveFolder } from '../../../features/docs/useDriveFiles';

interface MerchantDocsTabProps {
  merchantId: number;
}

/**
 * DriveBar — collapsible document section that sits above the inner tabs.
 * Shows the merchant's Google Drive file count and browser.
 */
function MerchantDocsTab({ merchantId }: MerchantDocsTabProps) {
  const [expanded, setExpanded] = useState(false);
  const { token: providerToken, loading: providerTokenLoading } = useGoogleDriveToken();

  const { data: driveFolder, isLoading: driveFolderLoading } = useMerchantDriveFolder(merchantId);
  // Count Drive files too (root folder). Shares the query key with DriveFileList,
  // so React Query dedupes — no extra fetch.
  const {
    data: driveFiles = [],
    isLoading: driveFilesLoading,
    error: driveFilesError,
  } = useDriveFiles(driveFolder, null, providerToken);

  const countLoading = providerTokenLoading || driveFolderLoading || driveFilesLoading;
  const driveFolderUrl = canonicalDriveFolderUrl(driveFolder);
  return (
    <div className="sky-drivebar">
      <div className="sky-drivebar__row">
        <button
          type="button"
          className="sky-drivebar__head"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} Documents`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="sky-drivebar__icon">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" x2="8" y1="13" y2="13" />
            <line x1="16" x2="8" y1="17" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <div className="sky-drivebar__label">
            <span className="sky-drivebar__title">Documents</span>
            <span className="sky-drivebar__count">
              {countLoading
                ? '…'
                : !providerToken
                  ? 'Connect Drive'
                  : driveFilesError
                  ? 'Drive unavailable'
                  : `${driveFiles.length} file${driveFiles.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </button>
        <button
          type="button"
          className="sky-drivebar__toggle"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} Documents`}
        >
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            className={`sky-drivebar__chevron ${expanded ? 'sky-drivebar__chevron--open' : ''}`}
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {driveFolderUrl && (
          <a
            className="sky-drivebar__open-drive"
            href={driveFolderUrl}
            target="_blank"
            rel="noreferrer"
            onMouseDown={(event) => event.preventDefault()}
            title="Open the merchant folder in Google Drive"
            aria-label="Open the merchant folder in Google Drive"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#0F9D58" d="M7.68 2.75 1.12 14.1l3.28 5.68 6.56-11.36-3.28-5.67Z" />
              <path fill="#4285F4" d="M7.68 2.75h6.56l6.56 11.36h-6.56L7.68 2.75Z" />
              <path fill="#F4B400" d="m4.4 19.78 3.28-5.67h13.12l-3.28 5.67H4.4Z" />
            </svg>
          </a>
        )}
      </div>
      {expanded && (
        <div className="sky-drivebar__body">
          <div className="sky-drivebar__section">
            <div className="sky-drivebar__section-label">Google Drive</div>
            <DriveFileList
              key={merchantId}
              merchantId={merchantId}
              driveFolder={driveFolder}
              driveFolderLoading={driveFolderLoading}
              providerToken={providerToken}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(MerchantDocsTab);
